import { Component, computed, EventEmitter, Input, OnDestroy, OnInit, Output, signal, viewChild } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { AoFilterHeaderField, AoFilterHeaderSelect } from '../ao-filter-list-header.component';
import { MatMenuTrigger } from '@angular/material/menu';
import { toSignal } from '@angular/core/rxjs-interop';

/**
 * Filter popup menu for columns with distinct values.
 * The popup menu has a multi select for selecting which values should be filtered.
 * It also allows changing the sorting order and clearing the filter.
 */
@Component({
    selector: 'app-filter-multi-select',
    templateUrl: './filter-multi-select.component.html',
    styleUrls: ['./filter-multi-select.component.scss'],
    standalone: false,
})
export class FilterMultiSelectComponent implements OnInit {
    trigger = viewChild.required(MatMenuTrigger);

    /**
     * The field for which this filter is used
     */
    @Input() field!: AoFilterHeaderField;
    /**
     * The form control for the filter input
     */
    @Input() control!: UntypedFormControl;

    private _selectValues?: AoFilterHeaderSelect[] | undefined;
    get selectValues(): AoFilterHeaderSelect[] | undefined {
        return this._selectValues;
    }
    /**
     * The values for the multi select
     */
    @Input() set selectValues(value: AoFilterHeaderSelect[] | undefined) {
        this._selectValues = value;

        if (this.selectValues?.length) {
            this.setForm();
        }
    }

    /**
     * Emitted when the sort order changed
     */
    @Output() sortChange = new EventEmitter<void>();

    searchControl = new FormControl('');

    searchTerm = toSignal(this.searchControl.valueChanges, { initialValue: this.searchControl.value });
    shownOptions = computed(() => {
        const term = this.searchTerm()?.toLowerCase();
        if (!term) return this.field.selectValues ?? [];
        return this.field.selectValues?.filter((option) => option.name?.toLowerCase().includes(term)) ?? [];
    });

    form = new UntypedFormGroup({});
    formSet = false;
    public hasValue = true;
    public valueText = signal('');

    ngOnInit(): void {}

    apply() {
        const v = this.form.value;
        const newValue = this.selectValues?.filter((val) => v[val.value]).map((o) => o.value);
        this.hasValue = !!newValue?.length;
        this.valueText.set(
            this.selectValues && this.hasValue
                ? this.selectValues
                      .filter((val) => v[val.value])
                      .map((o) => o.name)
                      .join()
                : '',
        );
        this.control.setValue(newValue);
        this.trigger().closeMenu();
        this.searchControl.reset();
    }

    setForm(): void {
        if (this.selectValues?.length) {
            this.hasValue = !!this.control.value?.length;
            this.valueText.set(
                this.hasValue
                    ? this.selectValues
                          .filter((val) => this.control.value.includes(val.value))
                          .map((o) => o.name)
                          .join()
                    : '',
            );

            for (const option of this.selectValues) {
                this.form.addControl(
                    option.value,
                    new UntypedFormControl(this.control.value.includes(option.value) ? option.value : ''),
                    { emitEvent: false },
                );
            }

            this.formSet = true;
        }
    }

    clear(): void {
        this.searchControl.reset();
        this.form.reset();
        this.apply();
    }
}
